import { Drawer } from 'components/Drawer'
import { useCallback, useMemo, useState } from 'react'
import { SelectAccountsPanel } from 'screens/BrandKit/LinkedAccountsDrawer/SelectAccountsPanel'
import { LinkedAccountsDrawerProps } from 'screens/BrandKit/LinkedAccountsDrawer/Types'
import { Panel } from './consts'
import { ManageAccountsPanel } from 'screens/BrandKit/LinkedAccountsDrawer/ManageAccountsPanel'
import { useCreateBrandKitSource } from 'hooks/queries/useCreateBrandKitSource'
import { useRootStore } from 'stores/RootStore'
import { ExternalIntegration, ExternalIntegrationEnum } from 'api/clients/ExternalIntegrationsClient'
import { IntegrationAuth } from 'api/clients/IntegrationAuthsClient'
import { ConnectProviderPanel } from 'screens/BrandKit/LinkedAccountsDrawer/ConnectProviderPanel'
import { showToastMessage } from 'common/ToastMessage/ShowToastMessage'
import { pluralize } from 'utils'
import { Heading } from 'blaze-ui/Heading/Heading'

export const LinkedAccountsDrawer = ({
  brandKitId,
  initialPanel = Panel.SelectAccounts,
  ...props
}: LinkedAccountsDrawerProps) => {
  const [provider, setProvider] = useState<
    ExternalIntegrationEnum.Facebook | ExternalIntegrationEnum.Instagram | ExternalIntegrationEnum.LinkedIn | undefined
  >(undefined)
  const [integrationAuth, setIntegrationAuth] = useState<IntegrationAuth | undefined>(undefined)
  const [panel, setPanel] = useState(initialPanel)
  const { drawersStore } = useRootStore()
  const { mutateAsync: createBrandKitSource } = useCreateBrandKitSource({ mutationKey: 'createBrandKitSource' })
  const { closeAllDrawers, closeDrawer } = drawersStore

  const handleLink = useCallback(
    (selectedAccounts: ExternalIntegration[]) => {
      Promise.allSettled(
        selectedAccounts.map((integration) => {
          return createBrandKitSource({
            brandKitId,
            source: { type: 'ExternalIntegration', id: integration.id },
          })
        }),
      ).then((results) => {
        const successes = results.filter((result) => result.status === 'fulfilled').length
        if (successes > 0) {
          showToastMessage(`${successes} ${pluralize('Account', successes)} added to your Brand Kit!`)
        }
      })
      setIntegrationAuth(undefined)
      setPanel(Panel.ManageAccounts)
    },
    [brandKitId, createBrandKitSource],
  )

  const currentPanel = useMemo(() => {
    if (provider) return Panel.ConnectProvider

    return panel
  }, [provider, panel])

  const handleBack = useCallback(() => {
    if (currentPanel === Panel.ConnectProvider) {
      setProvider(undefined)
      setPanel(Panel.SelectAccounts)
      return
    }

    closeDrawer()
  }, [currentPanel, closeDrawer])

  const heading = useMemo(() => {
    if (currentPanel === Panel.ManageAccounts) {
      return 'Linked Accounts'
    }

    if (currentPanel === Panel.SelectAccounts) {
      return 'Connect Accounts'
    }

    if (provider === ExternalIntegrationEnum.LinkedIn) {
      return 'Connect LinkedIn Account'
    }

    return 'Connect Meta Account'
  }, [currentPanel, provider])

  return (
    <Drawer.Root {...props} activeSnapPoint={1} snapPoints={[1]}>
      <Drawer.Portal>
        <Drawer.Content isSnappedTop fullHeight>
          <Drawer.InnerContent isSnappedTop fullHeight>
            <Drawer.Top>
              <Drawer.Back variant="ghost" onPress={handleBack} position="left">
                Back
              </Drawer.Back>
              <Drawer.Title asChild>
                <Heading level={5} lineClamp={1}>
                  {heading}
                </Heading>
              </Drawer.Title>
              <Drawer.Back variant="ghost" onPress={closeAllDrawers} position="right">
                Cancel
              </Drawer.Back>
            </Drawer.Top>
            <Drawer.Container>
              {currentPanel === Panel.ManageAccounts && (
                <ManageAccountsPanel
                  brandKitId={brandKitId}
                  onLinkMoreAccounts={() => setPanel(Panel.SelectAccounts)}
                />
              )}
              {currentPanel === Panel.SelectAccounts && (
                <SelectAccountsPanel
                  brandKitId={brandKitId}
                  onLink={handleLink}
                  integrationAuth={integrationAuth}
                  onSelectFacebook={() => setProvider(ExternalIntegrationEnum.Facebook)}
                  onSelectInstagram={() => setProvider(ExternalIntegrationEnum.Instagram)}
                  onSelectLinkedIn={() => setProvider(ExternalIntegrationEnum.LinkedIn)}
                />
              )}
              {currentPanel === Panel.ConnectProvider && !!provider && (
                <ConnectProviderPanel
                  onIntegrationAuthCreated={(integrationAuth) => {
                    setIntegrationAuth(integrationAuth)
                    setProvider(undefined)
                    setPanel(Panel.SelectAccounts)
                  }}
                  provider={provider}
                />
              )}
            </Drawer.Container>
          </Drawer.InnerContent>
        </Drawer.Content>
        <Drawer.Overlay />
      </Drawer.Portal>
    </Drawer.Root>
  )
}
